<template>
    <div>
        <page-header :title="$t('search.title')" />
        <ion-button class="filter-button" shape="round" color="primary" @click="toggleFilter">
            {{ isFilterOpen ? $t('search.hideFilter') : $t('search.filter') }}
        </ion-button>
        <ion-list class="search-options ion-margin-top w-100" :style="isFilterOpen ? 'display:block' : 'display:none'">
            <ion-grid>
                <ion-row>
                    <ion-col size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <options-selector
                            :key="renderKey + 1"
                            :options="genderOptions"
                            :label="$t('options.gender')"
                            @onChange="handleChange"
                            type="gender"
                            required="true"
                            :defaultValue="oppositeGender"
                        />
                    </ion-col>
                    <ion-col size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <options-selector
                            :key="renderKey + 2"
                            :options="purposeOptions"
                            :label="$t('options.purpose')"
                            @onChange="handleChange"
                            type="purpose"
                            required="true"
                            :defaultValue="selectedOptions['purpose']"
                        />
                    </ion-col>
                    <ion-col size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <options-selector
                            :key="renderKey + 3"
                            :options="countryOptions"
                            :label="$t('options.country')"
                            @onChange="handleChange"
                            type="country"
                            required="true"
                            :defaultValue="selectedOptions['country']"
                        />
                    </ion-col>
                    <ion-col size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <options-selector
                            :key="renderKey + 4"
                            :options="originCountryOptions"
                            :label="$t('options.originCountry')"
                            @onChange="handleChange"
                            type="origin-country"
                            required="true"
                            :defaultValue="selectedOptions['origin-country']"
                        />
                    </ion-col>
                    <ion-col size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <options-selector
                            :key="renderKey + 5"
                            :options="ageOptions"
                            :label="$t('options.age')"
                            @onChange="handleChange"
                            type="age"
                            required="true"
                            :defaultValue="selectedOptions['age']"
                        />
                    </ion-col>
                    <ion-col class="online-checkbox-wrapper" size-xs="12" size-sm="12" size-md="6" size-xl="4">
                        <div class="online-checkbox">
                            <checkbox
                                :key="renderKey + 6"
                                ref="onlineCheckbox"
                                :label="$t('search.currentlyOnline')"
                                checked="false"
                                @onChange="handleOnlineStatusChange"
                            />
                        </div>
                    </ion-col>
                </ion-row>
            </ion-grid>

            <ion-button @click="handleClear" shape="round" color="primary" class="search-button ion-padding">
                {{ $t('search.clear') }}
            </ion-button>
            <ion-button @click="handleSearch(false)" shape="round" color="primary" class="search-button ion-padding">{{
                $t('master.search')
            }}</ion-button>
        </ion-list>
        <p class="no-search-results" v-if="noSearchResults">{{ $t('search.noSearchResults') }}</p>
        <ion-row v-else-if="!isLoading" class="w-100 ion-margin-top">
            <ion-col v-for="user in users" :key="user.id" size-xs="6" size-sm="6" size-md="4" size-xl="3">
                <user-card :user="user" />
            </ion-col>
            <ion-infinite-scroll v-if="isMobile" @ionInfinite="loadMore" threshold="100px" position="bottom">
                <ion-infinite-scroll-content></ion-infinite-scroll-content>
            </ion-infinite-scroll>
            <ion-button
                class="more-button"
                v-if="(!isMobile || windowWidth >= 1200) && moreUsers && !isSearching"
                @click="handleSearch(true)"
                shape="round"
            >
                {{ $t('master.more') }}
            </ion-button>
            <div class="spinner-wrapper" v-if="isSearching">
                <ion-spinner color="primary"></ion-spinner>
            </div>
        </ion-row>
        <div class="ion-margin-top" v-else>
            <ion-spinner color="primary"></ion-spinner>
        </div>
        <ad-leaderboard />
    </div>
</template>

<script>
import OptionsSelector from '@/components/OptionsSelector'
import Checkbox from '@/components/Checkbox'
import AxiosController from '@/controllers/AxiosController'
import UserCard from '@/components/UserCard'
import PageHeader from '@/components/PageHeader'
import { mapGetters } from 'vuex'
import { isMobile } from 'mobile-device-detect'
import AdLeaderboard from '@/components/AdLeaderboard'

export default {
    name: 'search',
    components: { OptionsSelector, Checkbox, UserCard, PageHeader, AdLeaderboard },
    data() {
        return {
            genderOptions: [],
            countryOptions: [],
            ageOptions: [],
            purposeOptions: [],
            originCountryOptions: [],
            selectedOptions: [],
            users: [],
            onlineOnly: false,
            currentPage: 1,
            userGender: '',
            oppositeGender: 'female',
            isFilterOpen: true,
            noSearchResults: false,
            isMobile,
            renderKey: 0,
            moreUsers: false,
            isSearching: false,
            isLoading: true
        }
    },
    mounted() {
        const params = {
            type: ['gender', 'country', 'origin-country', 'purpose', 'age']
        }
        AxiosController.get('/options', { params }).then(res => {
            this.genderOptions = res.data.filter(option => option.type === 'gender')
            this.countryOptions = res.data.filter(option => option.type === 'country')
            this.purposeOptions = res.data.filter(option => option.type === 'purpose')
            this.ageOptions = res.data.filter(option => option.type === 'age')
            this.originCountryOptions = res.data.filter(option => option.type === 'origin-country')
            this.userGender = this.user ? this.user.options.filter(option => option.type === 'gender')[0].value : 'male'
            this.oppositeGender = this.userGender === 'male' ? 'female' : 'male'
            this.selectedOptions['gender'] = String(this.genderOptions.find(option => option.value === this.oppositeGender).id)
            this.handleSearch(false)
        })
    },
    methods: {
        handleChange: function(option) {
            this.selectedOptions[option.type] = option.value
        },
        handleClear: function() {
            this.selectedOptions = []
            this.onlineOnly = false
            this.selectedOptions['gender'] = String(this.genderOptions.find(option => option.value === this.oppositeGender).id)
            this.$nextTick(() => {
                this.renderKey = this.renderKey + 1
            })
        },
        handleOnlineStatusChange: function(value) {
            this.onlineOnly = value.checked
        },
        handleSearch: function(isLoadMore) {
            if (!isLoadMore) {
                this.toggleFilter()
                this.isLoading = true
            }
            this.isSearching = true
            this.noSearchResults = false
            this.currentPage = isLoadMore ? this.currentPage + 1 : 1
            const options = Object.values(this.selectedOptions)
            const params = {
                options: options,
                onlineOnly: this.onlineOnly,
                page: this.currentPage
            }
            AxiosController.get('/search', { params }).then(res => {
                this.moreUsers = res.data.length === 12 ? true : false
                if (isLoadMore) {
                    this.users = [...this.users, ...res.data]
                } else {
                    this.users = res.data
                }

                if (this.users.length == 0) {
                    this.noSearchResults = true
                }
                this.isLoading = false
                this.isSearching = false
            })
        },
        loadMore(event) {
            if (!this.moreUsers) {
                event.target.complete()
                return
            }
            this.handleSearch(true)
            event.target.complete()
        },
        toggleFilter() {
            this.isFilterOpen = !this.isFilterOpen
        }
    },
    computed: mapGetters(['user'])
}
</script>

<style lang="scss">
.filter-button {
    width: 100px;
}

.more-button,
.spinner-wrapper {
    margin: 20px auto;
}

.search-options {
    background-color: #f9f9f9;
    margin-bottom: 20px;
    display: none;

    ion-item {
        --background: #f9f9f9;
    }

    .search-button {
        width: 50%;
        margin: 30px 0;
    }

    .online-checkbox {
        border-bottom: 1px solid #aaaaaa;
    }
}

.no-search-results {
    font-size: 18px;
}

@media (min-width: 768px) {
    .filter-button {
        width: 20%;
        font-size: 120%;
        --border-radius: 40px;
    }
    .search-button {
        width: 30% !important;
        font-size: 120%;
        --border-radius: 50px;
    }
    .online-checkbox-wrapper {
        position: relative;

        .online-checkbox {
            position: relative;
            top: 22px;
        }
    }
    .no-search-results {
        font-size: 22px;
        margin-top: 30px;
    }
}

@media (min-width: 1200px) {
    .online-checkbox {
        top: 1.5px !important;
    }
    .search-button {
        width: 20% !important;
    }
}
</style>
